// frontend/src/components/ClientExtraFieldsModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, Row, Col, Form, InputGroup } from 'react-bootstrap';
import clientExtraFieldService from '../services/clientExtraFieldService';
import ProtectedSection from './ProtectedSection';

// parse safe côté front (ceinture)
const normalizeOptions = (opt) => {
  if (opt == null) return { choices: [] };
  if (typeof opt === 'string') {
    try { opt = JSON.parse(opt); } catch { return { choices: [] }; }
  }
  if (typeof opt !== 'object') return { choices: [] };
  let choices = opt.choices;
  if (typeof choices === 'string') {
    choices = choices.split(',').map(s => s.trim()).filter(Boolean);
  } else if (!Array.isArray(choices)) {
    choices = [];
  }
  return { ...opt, choices: choices.map(String) };
};

export default function ClientExtraFieldsModal({ show, onHide }) {
  const EMPTY = useMemo(() => ({
    id: undefined,
    code: '',
    label: '',
    type: 'text',            // 'text' | 'number' | 'date' | 'select' | 'boolean'
    required: false,
    active: true,
    sortOrder: 0,
    options: { choices: [] }, // pour 'select'
  }), []);

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [mode, setMode] = useState('create'); // 'create' | 'edit'
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const list = await clientExtraFieldService.list();
      // normaliser options au cas où
      const normalized = list.map(it => ({ ...it, options: normalizeOptions(it.options) }));
      // tri local par sortOrder puis id
      normalized.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));
      setItems(normalized);
      setError('');
    } catch (e) {
      setItems([]);
      setError('Impossible de charger les champs.');
    }
  };

  useEffect(() => {
    if (show) {
      load().then(() => {
        setForm(EMPTY);
        setMode('create');
        setError('');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const resetForm = () => {
    setForm(EMPTY);
    setMode('create');
    setError('');
  };

  const onEdit = (it) => {
    const safe = normalizeOptions(it.options);
    setForm({
      id: it.id,
      code: it.code,
      label: it.label,
      type: it.type,
      required: !!it.required,
      active: !!it.active,
      sortOrder: it.sortOrder ?? 0,
      options: safe, // { choices: [...] }
    });
    setMode('edit');
    setError('');
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Supprimer le champ “${it.label}” ?`)) return;
    try {
      await clientExtraFieldService.remove(it.id);
      await load();
      if (form.id === it.id) resetForm();
    } catch (e) {
      setError('Suppression impossible.');
    }
  };

  const toChoicesString = (opts) => {
    const arr = (opts?.choices || []).map(String);
    return arr.join(', ');
  };
  const fromChoicesString = (str) => {
    if (!str) return [];
    return str
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  };

  const onChange = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // validations simples
    if (!form.code.trim()) return setError('Code requis.');
    if (!/^[a-z0-9_\-]+$/i.test(form.code.trim())) return setError('Code: lettres/chiffres/underscore/tiret uniquement.');
    if (!form.label.trim()) return setError('Libellé requis.');

    const payload = {
      code: form.code.trim(),
      label: form.label.trim(),
      type: form.type,
      required: !!form.required,
      active: !!form.active,
      sortOrder: Number(form.sortOrder || 0),
      options: form.type === 'select'
        ? { choices: fromChoicesString(form.options?.choicesString ?? toChoicesString(form.options)) }
        : null
    };

    try {
      if (mode === 'create') {
        await clientExtraFieldService.create(payload);
      } else {
        await clientExtraFieldService.update(form.id, payload);
      }
      await load();
      resetForm();
    } catch (e) {
      setError('Enregistrement impossible.');
    }
  };

  const moveUp = async (it) => {
    const idx = items.findIndex(x => x.id === it.id);
    if (idx <= 0) return;
    const prev = items[idx - 1];
    try {
      await clientExtraFieldService.update(prev.id, { ...prev, sortOrder: it.sortOrder });
      await clientExtraFieldService.update(it.id, { ...it, sortOrder: prev.sortOrder });
      await load();
    } catch (e) {
      setError('Ré-ordonnancement impossible.');
    }
  };

  const moveDown = async (it) => {
    const idx = items.findIndex(x => x.id === it.id);
    if (idx < 0 || idx >= items.length - 1) return;
    const next = items[idx + 1];
    try {
      await clientExtraFieldService.update(next.id, { ...next, sortOrder: it.sortOrder });
      await clientExtraFieldService.update(it.id, { ...it, sortOrder: next.sortOrder });
      await load();
    } catch (e) {
      setError('Ré-ordonnancement impossible.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Gérer les champs supplémentaires</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}

        <Row>
          {/* Liste */}
          <Col md={7} className="mb-3">
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Ordre</th>
                  <th>Libellé</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Oblig.</th>
                  <th>Actif</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id}>
                    <td>{it.sortOrder}</td>
                    <td>{it.label}</td>
                    <td><code>{it.code}</code></td>
                    <td>{it.type}</td>
                    <td>{it.required ? 'Oui' : 'Non'}</td>
                    <td>{it.active ? 'Oui' : 'Non'}</td>
                    <td>
                      <ProtectedSection code="CLIENT_FIELD_UPDATE">
                        <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => moveUp(it)}>▲</Button>
                        <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => moveDown(it)}>▼</Button>
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => onEdit(it)}>Éditer</Button>
                      </ProtectedSection>
                      <ProtectedSection code="CLIENT_FIELD_DELETE">
                        <Button variant="outline-danger" size="sm" onClick={() => onDelete(it)}>Supprimer</Button>
                      </ProtectedSection>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted">Aucun champ.</td></tr>
                )}
              </tbody>
            </Table>
          </Col>

          {/* Formulaire */}
          <Col md={5}>
            <Form onSubmit={submit}>
              <h6 className="mb-3">{mode === 'create' ? 'Créer un champ' : 'Modifier le champ'}</h6>

              <Form.Group className="mb-2" controlId="f-label">
                <Form.Label>Libellé *</Form.Label>
                <Form.Control
                  value={form.label}
                  onChange={e => onChange({ label: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="f-code">
                <Form.Label>Code *</Form.Label>
                <Form.Control
                  value={form.code}
                  onChange={e => onChange({ code: e.target.value })}
                  disabled={mode === 'edit'} // éviter de casser les liaisons
                />
                <Form.Text className="text-muted">ex: <code>profession</code>, <code>niveau</code>, <code>adresse_2</code>…</Form.Text>
              </Form.Group>

              <Row className="mb-2">
                <Col md={6}>
                  <Form.Group controlId="f-type">
                    <Form.Label>Type *</Form.Label>
                    <Form.Select
                      value={form.type}
                      onChange={e => onChange({ type: e.target.value })}
                    >
                      <option value="text">Texte</option>
                      <option value="number">Nombre</option>
                      <option value="date">Date</option>
                      <option value="select">Sélection</option>
                      <option value="boolean">Booléen</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="f-sort">
                    <Form.Label>Ordre</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.sortOrder}
                      onChange={e => onChange({ sortOrder: Number(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {form.type === 'select' && (
                <Form.Group className="mb-2" controlId="f-choices">
                  <Form.Label>Options (séparées par des virgules)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={
                      form.options?.choicesString ??
                      (Array.isArray(form.options?.choices) ? form.options.choices.join(', ') : '')
                    }
                    onChange={e => onChange({ options: { ...form.options, choicesString: e.target.value } })}
                  />
                </Form.Group>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="f-required"
                    label="Obligatoire"
                    checked={!!form.required}
                    onChange={e => onChange({ required: e.target.checked })}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="f-active"
                    label="Actif"
                    checked={!!form.active}
                    onChange={e => onChange({ active: e.target.checked })}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={resetForm}>Réinitialiser</Button>
                {mode === 'create' ? (
                  <ProtectedSection code="CLIENT_FIELD_CREATE">
                    <Button type="submit" variant="primary">Créer</Button>
                  </ProtectedSection>
                ) : (
                  <ProtectedSection code="CLIENT_FIELD_UPDATE">
                    <Button type="submit" variant="primary">Enregistrer</Button>
                  </ProtectedSection>
                )}
              </div>
            </Form>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}
